import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Database, Json } from "@/integrations/supabase/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { AlertTriangle, ArrowLeft, Eye, History, Layers3, Save, ShieldAlert, ShieldCheck, Sparkles, TimerReset } from "lucide-react";

type AgentRow = Database["public"]["Tables"]["syra_ai_agents"]["Row"];
type HistoryRow = Database["public"]["Tables"]["syra_agent_persona_history"]["Row"];
type PresetRow = Database["public"]["Tables"]["syra_persona_presets"]["Row"];
type DriftEventRow = Database["public"]["Tables"]["syra_persona_drift_events"]["Row"];
type PromptTraceRow = Database["public"]["Tables"]["syra_prompt_composition_traces"]["Row"];
type GoldenRunRow = Database["public"]["Tables"]["golden_set_runs"]["Row"];

type PersonaGoldenSummary = {
  persona_consistency_score: number;
  cross_layer_conflict_rate: number;
  modulation_within_bounds_rate: number;
  integrity_pass_rate?: number;
  mood_fit_score?: number;
};

type Personality = {
  agent_id: string;
  response_frequency: number;
  response_length: "curta" | "média" | "longa" | "adaptativa";
  formality: number;
  proactivity: number;
  objectivity: number;
  social_posture: "neutra" | "acolhedora" | "firme" | "provocadora" | "mediadora";
  caution_level: number;
  question_vs_assert: number;
  mediation_style: "passiva" | "ativa" | "arbitral" | "silenciosa";
  temperature_override: number | null;
};

type PersonaV19 = {
  persona_base: {
    identity?: string;
    tone?: string;
    response_style?: string;
    boundaries?: string;
  };
  persona_base_version: number;
  cultural_register: string;
  allow_drift_bounds: Record<string, number>;
  enabled_skills: string[];
  integrity_flags: {
    no_sycophancy: boolean;
    no_self_reference: boolean;
    preserve_factuality: boolean;
  };
};

const DEFAULT: Omit<Personality, "agent_id"> = {
  response_frequency: 50,
  response_length: "adaptativa",
  formality: 50,
  proactivity: 40,
  objectivity: 60,
  social_posture: "acolhedora",
  caution_level: 60,
  question_vs_assert: 40,
  mediation_style: "ativa",
  temperature_override: null,
};

const DEFAULT_PERSONA_V19: PersonaV19 = {
  persona_base: {
    identity: "Assistente do grupo com foco em utilidade, clareza e segurança.",
    tone: "Clara, cordial e objetiva.",
    response_style: "Adapta profundidade ao contexto sem inflar a resposta.",
    boundaries: "Não inventa fatos, não força intimidade e não se coloca como protagonista.",
  },
  persona_base_version: 1,
  cultural_register: "adaptativo",
  allow_drift_bounds: {
    formality: 10,
    proactivity: 10,
    objectivity: 10,
    caution_level: 10,
    question_vs_assert: 10,
    response_frequency: 10,
  },
  enabled_skills: [],
  integrity_flags: {
    no_sycophancy: true,
    no_self_reference: true,
    preserve_factuality: true,
  },
};

const CULTURAL_REGISTER_OPTIONS = ["adaptativo", "executivo", "comunitário", "educacional", "técnico"] as const;
const AVAILABLE_SKILLS = ["chat", "memory_search", "factual_search", "summarize", "moderation", "planner", "support", "sales"] as const;
const DRIFT_FIELDS = ["formality", "proactivity", "objectivity", "caution_level", "question_vs_assert", "response_frequency"] as const;

function band(n: number, low: string, mid: string, high: string) {
  if (n <= 33) return low;
  if (n <= 66) return mid;
  return high;
}

function asRecord(value: Json | null | undefined): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function normalizePersona(agent: Partial<AgentRow> | null | undefined): PersonaV19 {
  const base = asRecord(agent?.persona_base as Json | null);
  const bounds = asRecord(agent?.allow_drift_bounds as Json | null);
  const integrity = asRecord(agent?.integrity_flags as Json | null);

  return {
    persona_base: {
      ...DEFAULT_PERSONA_V19.persona_base,
      identity: typeof base.identity === "string" ? base.identity : DEFAULT_PERSONA_V19.persona_base.identity,
      tone: typeof base.tone === "string" ? base.tone : DEFAULT_PERSONA_V19.persona_base.tone,
      response_style: typeof base.response_style === "string" ? base.response_style : DEFAULT_PERSONA_V19.persona_base.response_style,
      boundaries: typeof base.boundaries === "string" ? base.boundaries : DEFAULT_PERSONA_V19.persona_base.boundaries,
    },
    persona_base_version: Number(agent?.persona_base_version ?? DEFAULT_PERSONA_V19.persona_base_version),
    cultural_register: typeof agent?.cultural_register === "string" ? agent.cultural_register : DEFAULT_PERSONA_V19.cultural_register,
    allow_drift_bounds: {
      ...DEFAULT_PERSONA_V19.allow_drift_bounds,
      ...Object.fromEntries(Object.entries(bounds).map(([key, value]) => [key, Number(value)])),
    },
    enabled_skills: Array.isArray(agent?.enabled_skills) ? agent.enabled_skills : [],
    integrity_flags: {
      no_sycophancy: integrity.no_sycophancy !== false,
      no_self_reference: integrity.no_self_reference !== false,
      preserve_factuality: integrity.preserve_factuality !== false,
    },
  };
}

function normalizePersonality(row: Record<string, unknown> | null | undefined, agentId: string): Personality {
  return {
    agent_id: agentId,
    response_frequency: Number(row?.response_frequency ?? DEFAULT.response_frequency),
    response_length: (row?.response_length as Personality["response_length"]) ?? DEFAULT.response_length,
    formality: Number(row?.formality ?? DEFAULT.formality),
    proactivity: Number(row?.proactivity ?? DEFAULT.proactivity),
    objectivity: Number(row?.objectivity ?? DEFAULT.objectivity),
    social_posture: (row?.social_posture as Personality["social_posture"]) ?? DEFAULT.social_posture,
    caution_level: Number(row?.caution_level ?? DEFAULT.caution_level),
    question_vs_assert: Number(row?.question_vs_assert ?? DEFAULT.question_vs_assert),
    mediation_style: (row?.mediation_style as Personality["mediation_style"]) ?? DEFAULT.mediation_style,
    temperature_override: row?.temperature_override == null ? null : Number(row.temperature_override),
  };
}

function compilePersonaSpec(personality: Personality, persona: PersonaV19) {
  const activeIntegrity = Object.entries(persona.integrity_flags)
    .filter(([, enabled]) => enabled)
    .map(([key]) => key);

  return [
    "[PERSONA STUDIO V19]",
    `Registro cultural: ${persona.cultural_register}`,
    `Formalidade: ${band(personality.formality, "informal calibrada", "cordial profissional", "formal precisa")}`,
    `Proatividade: ${band(personality.proactivity, "reativa", "pontual", "antecipa necessidades")}`,
    `Objetividade: ${band(personality.objectivity, "conversacional", "equilibrada", "muito objetiva")}`,
    `Cautela: ${band(personality.caution_level, "responde direto", "valida quando necessário", "confirma antes")}`,
    `Pergunta vs afirma: ${band(personality.question_vs_assert, "mais assertiva", "equilibrada", "mais investigativa")}`,
    `Frequência: ${personality.response_frequency <= 20 ? "só quando invocada" : personality.response_frequency <= 50 ? "baixa" : personality.response_frequency <= 80 ? "média" : "alta"}`,
    `Tom base: ${persona.persona_base.tone ?? "—"}`,
    `Identidade: ${persona.persona_base.identity ?? "—"}`,
    `Estilo: ${persona.persona_base.response_style ?? "—"}`,
    `Limites: ${persona.persona_base.boundaries ?? "—"}`,
    `Skills: ${persona.enabled_skills.length ? persona.enabled_skills.join(", ") : "nenhuma explicitamente habilitada"}`,
    `Integridade: ${activeIntegrity.length ? activeIntegrity.join(", ") : "sem flags ativas"}`,
  ].join("\n");
}

function buildPreviewReply(personality: Personality, persona: PersonaV19) {
  const opening = personality.formality >= 65 ? "Olá, pessoal." : personality.formality <= 35 ? "Oi, gente!" : "Pessoal,";
  const stance = personality.objectivity >= 65
    ? "seguimos com um resumo direto e acionável"
    : personality.objectivity <= 35
    ? "posso organizar isso de um jeito leve e conversado"
    : "posso estruturar isso com clareza";
  const initiative = personality.proactivity >= 65
    ? "já deixo próximos passos sugeridos"
    : personality.proactivity <= 35
    ? "respondo só ao que foi pedido"
    : "sinalizo o essencial e, se fizer sentido, amplio";
  const question = personality.question_vs_assert >= 65
    ? "Antes de fechar, qual recorte vocês querem priorizar?"
    : "Fecho com uma recomendação prática a seguir.";

  return [
    opening,
    `${persona.persona_base.identity ?? ""} ${stance}. ${initiative}.`,
    `Tom esperado: ${persona.persona_base.tone ?? "—"}. Limite operacional: ${persona.persona_base.boundaries ?? "—"}.`,
    question,
  ].join(" ");
}

function formatDate(value: string) {
  return new Date(value).toLocaleString("pt-BR");
}

export default function AgentPersonality() {
  const { id } = useParams();
  const { toast } = useToast();
  const [agent, setAgent] = useState<Pick<AgentRow, "id" | "name" | "org_id"> | null>(null);
  const [data, setData] = useState<Personality | null>(null);
  const [personaV19, setPersonaV19] = useState<PersonaV19>(DEFAULT_PERSONA_V19);
  const [presets, setPresets] = useState<PresetRow[]>([]);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [driftEvents, setDriftEvents] = useState<DriftEventRow[]>([]);
  const [promptTraces, setPromptTraces] = useState<PromptTraceRow[]>([]);
  const [goldenRuns, setGoldenRuns] = useState<GoldenRunRow[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState<string>("none");
  const [historyNote, setHistoryNote] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [goldenRunning, setGoldenRunning] = useState(false);

  useEffect(() => {
    if (!id) return;

    (async () => {
      setLoading(true);
      const [{ data: agentRaw, error: agentError }, { data: persRaw, error: persError }] = await Promise.all([
        supabase
          .from("syra_ai_agents_safe")
          .select("id, name, org_id, persona_base, persona_base_version, cultural_register, allow_drift_bounds, enabled_skills, integrity_flags")
          .eq("id", id)
          .maybeSingle(),
        supabase.from("agent_personality").select("*").eq("agent_id", id).maybeSingle(),
      ]);

      if (agentError || !agentRaw) {
        toast({ title: "Agente não encontrado", description: agentError?.message ?? "Não foi possível carregar o agente.", variant: "destructive" });
        setLoading(false);
        return;
      }

      if (persError) {
        toast({ title: "Falha ao carregar personalidade", description: persError.message, variant: "destructive" });
      }

      const nextAgent = agentRaw as Pick<AgentRow, "id" | "name" | "org_id"> & Partial<AgentRow>;
      setAgent({ id: nextAgent.id, name: nextAgent.name, org_id: nextAgent.org_id });
      setData(normalizePersonality((persRaw ?? null) as Record<string, unknown> | null, id));
      setPersonaV19(normalizePersona(nextAgent));

      const [presetRes, historyRes, driftRes, traceRes, goldenRes] = await Promise.all([
        supabase
          .from("syra_persona_presets")
          .select("*")
          .eq("org_id", nextAgent.org_id)
          .order("is_system", { ascending: false })
          .order("name", { ascending: true }),
        supabase
          .from("syra_agent_persona_history")
          .select("*")
          .eq("agent_id", id)
          .order("created_at", { ascending: false })
          .limit(20),
        supabase
          .from("syra_persona_drift_events")
          .select("*")
          .eq("agent_id", id)
          .order("detected_at", { ascending: false })
          .limit(12),
        supabase
          .from("syra_prompt_composition_traces")
          .select("*")
          .eq("agent_id", id)
          .order("created_at", { ascending: false })
          .limit(24),
        supabase
          .from("golden_set_runs")
          .select("*")
          .eq("org_id", nextAgent.org_id)
          .ilike("run_label", `persona_golden_%`)
          .order("created_at", { ascending: false })
          .limit(8),
      ]);

      if (presetRes.error) {
        toast({ title: "Falha ao carregar presets", description: presetRes.error.message, variant: "destructive" });
      } else {
        setPresets((presetRes.data ?? []) as PresetRow[]);
      }

      if (historyRes.error) {
        toast({ title: "Falha ao carregar histórico", description: historyRes.error.message, variant: "destructive" });
      } else {
        setHistory((historyRes.data ?? []) as HistoryRow[]);
      }

      if (driftRes.error) {
        toast({ title: "Falha ao carregar drift", description: driftRes.error.message, variant: "destructive" });
      } else {
        setDriftEvents((driftRes.data ?? []) as DriftEventRow[]);
      }

      if (traceRes.error) {
        toast({ title: "Falha ao carregar traces", description: traceRes.error.message, variant: "destructive" });
      } else {
        setPromptTraces((traceRes.data ?? []) as PromptTraceRow[]);
      }

      if (goldenRes?.error) {
        toast({ title: "Falha ao carregar golden set", description: goldenRes.error.message, variant: "destructive" });
      } else {
        setGoldenRuns((goldenRes?.data ?? []) as GoldenRunRow[]);
      }

      setLoading(false);
    })();
  }, [id, toast]);

  const personaSpec = useMemo(() => (data ? compilePersonaSpec(data, personaV19) : ""), [data, personaV19]);
  const previewReply = useMemo(() => (data ? buildPreviewReply(data, personaV19) : ""), [data, personaV19]);
  const activeIntegrity = useMemo(
    () => Object.entries(personaV19.integrity_flags).filter(([, enabled]) => enabled).map(([key]) => key),
    [personaV19.integrity_flags],
  );
  const traceSummary = useMemo(() => {
    const withLatency = promptTraces.filter((trace) => typeof trace.composition_latency_ms === "number");
    const avgLatency = withLatency.length
      ? withLatency.reduce((sum, trace) => sum + Number(trace.composition_latency_ms ?? 0), 0) / withLatency.length
      : null;
    const avgChars = promptTraces.length
      ? promptTraces.reduce((sum, trace) => sum + (trace.final_prompt_excerpt?.length ?? 0), 0) / promptTraces.length
      : null;
    const layerCounts = new Map<string, number>();

    promptTraces.forEach((trace) => {
      const layers = Array.isArray(trace.layers) ? trace.layers : [];
      layers.forEach((layer) => {
        const key = layer && typeof layer === "object" && "key" in layer ? String((layer as { key?: unknown }).key ?? "unknown") : "unknown";
        layerCounts.set(key, (layerCounts.get(key) ?? 0) + 1);
      });
    });

    return {
      total: promptTraces.length,
      avgLatency,
      avgChars,
      topLayers: Array.from(layerCounts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 4),
      lastSeenAt: promptTraces[0]?.created_at ?? null,
    };
  }, [promptTraces]);
  const driftSummary = useMemo(() => {
    const openEvents = driftEvents.filter((event) => !event.resolved_at);
    const avgScore = driftEvents.length
      ? driftEvents.reduce((sum, event) => sum + Number(event.drift_score ?? 0), 0) / driftEvents.length
      : null;
    return {
      total: driftEvents.length,
      open: openEvents.length,
      avgScore,
      last: driftEvents[0] ?? null,
    };
  }, [driftEvents]);
  const historySummary = useMemo(() => ({
    total: history.length,
    lastSavedAt: history[0]?.created_at ?? null,
    presetCount: history.filter((item) => item.source === "persona_studio_preset").length,
  }), [history]);
  const latestGoldenMetrics = useMemo(() => {
    const details = asRecord(goldenRuns[0]?.details);
    const metrics = details.metrics;
    return (metrics && typeof metrics === "object" && !Array.isArray(metrics) ? metrics : {}) as Partial<PersonaGoldenSummary>;
  }, [goldenRuns]);

  function applyPreset(presetId: string) {
    setSelectedPresetId(presetId);
    if (presetId === "none") return;
    const preset = presets.find((item) => item.id === presetId);
    if (!preset) return;

    setPersonaV19({
      persona_base: {
        ...DEFAULT_PERSONA_V19.persona_base,
        ...asRecord(preset.persona_base),
      },
      persona_base_version: Math.max(personaV19.persona_base_version, 1),
      cultural_register: preset.cultural_register,
      allow_drift_bounds: {
        ...DEFAULT_PERSONA_V19.allow_drift_bounds,
        ...Object.fromEntries(Object.entries(asRecord(preset.allow_drift_bounds)).map(([key, value]) => [key, Number(value)])),
      },
      enabled_skills: Array.isArray(preset.enabled_skills) ? preset.enabled_skills : [],
      integrity_flags: {
        no_sycophancy: asRecord(preset.integrity_flags).no_sycophancy !== false,
        no_self_reference: asRecord(preset.integrity_flags).no_self_reference !== false,
        preserve_factuality: asRecord(preset.integrity_flags).preserve_factuality !== false,
      },
    });

    setHistoryNote((current) => current || `Preset aplicado: ${preset.name}`);
  }

  async function save() {
    if (!id || !data || !agent?.org_id) return;
    setSaving(true);

    const { data: authData } = await supabase.auth.getUser();
    const changedBy = authData.user?.id ?? null;

    const [personalityResult, agentResult, historyResult] = await Promise.all([
      supabase.from("agent_personality").upsert({ ...data, agent_id: id }, { onConflict: "agent_id" }),
      supabase
        .from("syra_ai_agents")
        .update({
          persona_base: personaV19.persona_base,
          persona_base_version: personaV19.persona_base_version,
          cultural_register: personaV19.cultural_register,
          allow_drift_bounds: personaV19.allow_drift_bounds,
          enabled_skills: personaV19.enabled_skills,
          integrity_flags: personaV19.integrity_flags,
        })
        .eq("id", id),
      supabase.from("syra_agent_persona_history").insert({
        agent_id: id,
        org_id: agent.org_id,
        changed_by: changedBy,
        source: selectedPresetId !== "none" ? "persona_studio_preset" : "persona_studio_manual",
        notes: historyNote.trim() || null,
        persona_base: personaV19.persona_base,
        persona_base_version: personaV19.persona_base_version,
        cultural_register: personaV19.cultural_register,
        allow_drift_bounds: personaV19.allow_drift_bounds,
        enabled_skills: personaV19.enabled_skills,
        integrity_flags: personaV19.integrity_flags,
      }),
    ]);

    setSaving(false);
    const error = personalityResult.error ?? agentResult.error ?? historyResult.error;

    if (error) {
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
      return;
    }

    const { data: refreshedHistory } = await supabase
      .from("syra_agent_persona_history")
      .select("*")
      .eq("agent_id", id)
      .order("created_at", { ascending: false })
      .limit(20);

    setHistory((refreshedHistory ?? []) as HistoryRow[]);
    setHistoryNote("");
    toast({ title: "Persona Studio atualizado", description: "Base, preview e histórico foram persistidos." });
  }

  async function runAudit() {
    if (!id) return;
    setAuditing(true);

    const { data: result, error } = await supabase.functions.invoke("persona-drift-detector", {
      body: { agent_id: id },
    });

    if (error) {
      setAuditing(false);
      toast({ title: "Falha ao rodar auditoria", description: error.message, variant: "destructive" });
      return;
    }

    const [driftRes, traceRes] = await Promise.all([
      supabase
        .from("syra_persona_drift_events")
        .select("*")
        .eq("agent_id", id)
        .order("detected_at", { ascending: false })
        .limit(12),
      supabase
        .from("syra_prompt_composition_traces")
        .select("*")
        .eq("agent_id", id)
        .order("created_at", { ascending: false })
        .limit(24),
    ]);

    if (!driftRes.error) setDriftEvents((driftRes.data ?? []) as DriftEventRow[]);
    if (!traceRes.error) setPromptTraces((traceRes.data ?? []) as PromptTraceRow[]);

    setAuditing(false);
    toast({
      title: "Auditoria executada",
      description: `${Array.isArray((result as { results?: unknown[] })?.results) ? (result as { results?: unknown[] }).results?.length ?? 0 : 0} persona(s) avaliadas.`,
    });
  }

  async function runGoldenSet() {
    if (!id || !agent?.org_id) return;
    setGoldenRunning(true);
    const { data: result, error } = await supabase.functions.invoke("persona-golden-runner", {
      body: { agent_id: id, org_id: agent.org_id },
    });

    if (error) {
      setGoldenRunning(false);
      toast({ title: "Falha ao rodar golden set", description: error.message, variant: "destructive" });
      return;
    }

    const { data: goldenRes } = await supabase
      .from("golden_set_runs")
      .select("*")
      .eq("org_id", agent.org_id)
      .ilike("run_label", `persona_golden_%`)
      .order("created_at", { ascending: false })
      .limit(8);

    setGoldenRuns((goldenRes ?? []) as GoldenRunRow[]);
    setGoldenRunning(false);
    const resultMetrics = (result && typeof result === "object" && !Array.isArray(result) && "metrics" in result)
      ? (result as { metrics?: Record<string, unknown> }).metrics
      : {};
    toast({
      title: "Golden set executado",
      description: `${Number(resultMetrics?.persona_consistency_score ?? 0).toFixed(2)} de consistência na rodada atual.`,
    });
  }

  if (loading || !data || !agent) {
    return <div className="p-6 text-muted-foreground">Carregando Persona Studio...</div>;
  }

  return (
    <div className="container mx-auto max-w-7xl p-6 space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <Button variant="ghost" size="sm" asChild className="-ml-3 w-fit">
            <Link to="/agents"><ArrowLeft className="mr-1 h-4 w-4" /> Voltar</Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Persona Studio v19 — {agent.name}</h1>
            <p className="text-sm text-muted-foreground">Base, preview e visualização do runtime em uma única superfície operacional.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">S43</Badge>
          <Badge variant="outline">S46</Badge>
          <Badge variant="outline">S47</Badge>
          <Badge variant="secondary">3 colunas</Badge>
          <Button variant="outline" onClick={runGoldenSet} disabled={goldenRunning}>
            <Sparkles className="mr-2 h-4 w-4" /> {goldenRunning ? "Executando golden..." : "Rodar golden set"}
          </Button>
          <Button variant="outline" onClick={runAudit} disabled={auditing}>
            <ShieldAlert className="mr-2 h-4 w-4" /> {auditing ? "Auditando..." : "Rodar auditoria"}
          </Button>
          <Button onClick={save} disabled={saving}>
            <Save className="mr-2 h-4 w-4" /> {saving ? "Salvando..." : "Salvar persona"}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.95fr_0.9fr]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Layers3 className="h-5 w-5 text-primary" /> Base</CardTitle>
            <CardDescription>Traits, preset, registro cultural, integridade e envelope de drift.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Preset</Label>
              <Select value={selectedPresetId} onValueChange={applyPreset}>
                <SelectTrigger>
                  <SelectValue placeholder="Aplicar preset" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Manual</SelectItem>
                  {presets.map((preset) => (
                    <SelectItem key={preset.id} value={preset.id}>{preset.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedPresetId !== "none" && presets.find((preset) => preset.id === selectedPresetId)?.description ? (
                <p className="text-xs text-muted-foreground">{presets.find((preset) => preset.id === selectedPresetId)?.description}</p>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <SliderRow label="Formalidade" value={data.formality} hint={band(data.formality, "Informal", "Cordial profissional", "Formal")} onChange={(value) => setData({ ...data, formality: value })} />
              <SliderRow label="Proatividade" value={data.proactivity} hint={band(data.proactivity, "Reativa", "Pontual", "Antecipa")} onChange={(value) => setData({ ...data, proactivity: value })} />
              <SliderRow label="Objetividade" value={data.objectivity} hint={band(data.objectivity, "Conversacional", "Equilibrada", "Muito objetiva")} onChange={(value) => setData({ ...data, objectivity: value })} />
              <SliderRow label="Cautela" value={data.caution_level} hint={band(data.caution_level, "Direta", "Valida", "Confirma antes")} onChange={(value) => setData({ ...data, caution_level: value })} />
              <SliderRow label="Pergunta vs afirma" value={data.question_vs_assert} hint={band(data.question_vs_assert, "Assertiva", "Mista", "Investigativa")} onChange={(value) => setData({ ...data, question_vs_assert: value })} />
              <SliderRow label="Frequência" value={data.response_frequency} hint={data.response_frequency <= 20 ? "Só quando invocada" : data.response_frequency <= 50 ? "Baixa" : data.response_frequency <= 80 ? "Média" : "Alta"} onChange={(value) => setData({ ...data, response_frequency: value })} />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Tamanho da resposta</Label>
                <Select value={data.response_length} onValueChange={(value: Personality["response_length"]) => setData({ ...data, response_length: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="curta">Curta</SelectItem>
                    <SelectItem value="média">Média</SelectItem>
                    <SelectItem value="longa">Longa</SelectItem>
                    <SelectItem value="adaptativa">Adaptativa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Registro cultural</Label>
                <Select value={personaV19.cultural_register} onValueChange={(value) => setPersonaV19({ ...personaV19, cultural_register: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CULTURAL_REGISTER_OPTIONS.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Postura social</Label>
                <Select value={data.social_posture} onValueChange={(value: Personality["social_posture"]) => setData({ ...data, social_posture: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="neutra">Neutra</SelectItem>
                    <SelectItem value="acolhedora">Acolhedora</SelectItem>
                    <SelectItem value="firme">Firme</SelectItem>
                    <SelectItem value="provocadora">Provocadora</SelectItem>
                    <SelectItem value="mediadora">Mediadora</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estilo de mediação</Label>
                <Select value={data.mediation_style} onValueChange={(value: Personality["mediation_style"]) => setData({ ...data, mediation_style: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="passiva">Passiva</SelectItem>
                    <SelectItem value="ativa">Ativa</SelectItem>
                    <SelectItem value="arbitral">Arbitral</SelectItem>
                    <SelectItem value="silenciosa">Silenciosa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>Persona base</Label>
              <Textarea value={personaV19.persona_base.identity ?? ""} onChange={(e) => setPersonaV19({ ...personaV19, persona_base: { ...personaV19.persona_base, identity: e.target.value } })} placeholder="Identidade operacional da agente" />
              <Textarea value={personaV19.persona_base.tone ?? ""} onChange={(e) => setPersonaV19({ ...personaV19, persona_base: { ...personaV19.persona_base, tone: e.target.value } })} placeholder="Tom base" />
              <Textarea value={personaV19.persona_base.response_style ?? ""} onChange={(e) => setPersonaV19({ ...personaV19, persona_base: { ...personaV19.persona_base, response_style: e.target.value } })} placeholder="Estilo de resposta" />
              <Textarea value={personaV19.persona_base.boundaries ?? ""} onChange={(e) => setPersonaV19({ ...personaV19, persona_base: { ...personaV19.persona_base, boundaries: e.target.value } })} placeholder="Limites e guardrails" />
            </div>

            <div className="space-y-3">
              <Label>Drift bounds</Label>
              <div className="grid gap-3 sm:grid-cols-2">
                {DRIFT_FIELDS.map((field) => (
                  <div key={field}>
                    <Label className="text-xs text-muted-foreground">{field}</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={personaV19.allow_drift_bounds[field] ?? 0}
                      onChange={(e) => setPersonaV19({
                        ...personaV19,
                        allow_drift_bounds: { ...personaV19.allow_drift_bounds, [field]: Number(e.target.value) },
                      })}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Skills habilitadas</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                {AVAILABLE_SKILLS.map((skill) => {
                  const checked = personaV19.enabled_skills.includes(skill);
                  return (
                    <label key={skill} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(next) => setPersonaV19({
                          ...personaV19,
                          enabled_skills: next
                            ? [...new Set([...personaV19.enabled_skills, skill])]
                            : personaV19.enabled_skills.filter((item) => item !== skill),
                        })}
                      />
                      <span>{skill}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Flags de integridade</Label>
              {Object.entries(personaV19.integrity_flags).map(([key, value]) => (
                <label key={key} className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm">
                  <Checkbox checked={value} onCheckedChange={(next) => setPersonaV19({ ...personaV19, integrity_flags: { ...personaV19.integrity_flags, [key]: !!next } })} />
                  <span>{key}</span>
                </label>
              ))}
            </div>

            <div>
              <Label>Nota da mudança</Label>
              <Textarea value={historyNote} onChange={(e) => setHistoryNote(e.target.value)} placeholder="Ex.: ajuste para grupos executivos / preset de mediação" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Preview</CardTitle>
            <CardDescription>Prévia textual do comportamento compilado e da resposta esperada.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2 rounded-md border border-border bg-muted/30 p-4">
              <div className="flex items-center gap-2 text-sm font-medium"><Eye className="h-4 w-4" /> Especificação compilada</div>
              <pre className="whitespace-pre-wrap text-xs text-muted-foreground">{personaSpec}</pre>
            </div>

            <div className="space-y-2 rounded-md border border-border bg-muted/30 p-4">
              <div className="text-sm font-medium">Preview de resposta</div>
              <p className="text-sm leading-6 text-foreground">{previewReply}</p>
            </div>

            <div className="space-y-3">
              <Label>Temperatura override</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="2"
                value={data.temperature_override ?? ""}
                onChange={(e) => setData({ ...data, temperature_override: e.target.value === "" ? null : Number(e.target.value) })}
                placeholder="Usar default do agente"
              />
              <p className="text-xs text-muted-foreground">0 = determinístico · 1 = padrão · acima de 1.2 tende a aumentar variação.</p>
            </div>

            <div className="space-y-3">
              <Label>Leitura rápida</Label>
              <div className="grid gap-2 sm:grid-cols-2">
                <MetricPill label="Registro" value={personaV19.cultural_register} />
                <MetricPill label="Tamanho" value={data.response_length} />
                <MetricPill label="Postura" value={data.social_posture} />
                <MetricPill label="Mediação" value={data.mediation_style} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Visualização</CardTitle>
            <CardDescription>Envelope ativo, inventário operacional e histórico das mudanças.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <div>
                <div className="text-sm font-medium">Integridade ativa</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {activeIntegrity.length ? activeIntegrity.map((flag) => <Badge key={flag} variant="secondary">{flag}</Badge>) : <Badge variant="outline">sem flags</Badge>}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium">Skills</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {personaV19.enabled_skills.length ? personaV19.enabled_skills.map((skill) => <Badge key={skill} variant="outline">{skill}</Badge>) : <Badge variant="outline">nenhuma</Badge>}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium">Envelope de drift</div>
              <div className="space-y-2">
                {DRIFT_FIELDS.map((field) => (
                  <div key={field} className="space-y-1">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{field}</span>
                      <span>±{personaV19.allow_drift_bounds[field] ?? 0}</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted">
                      <div className="h-2 rounded-full bg-primary" style={{ width: `${Math.min(100, Number(personaV19.allow_drift_bounds[field] ?? 0))}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium"><History className="h-4 w-4" /> Histórico recente</div>
              <ScrollArea className="h-[320px] pr-3">
                <div className="space-y-3">
                  {history.length ? history.map((item) => {
                    const itemIntegrity = Object.entries(asRecord(item.integrity_flags)).filter(([, enabled]) => !!enabled).map(([key]) => key);
                    return (
                      <div key={item.id} className="rounded-md border border-border p-3 text-sm">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-medium">{item.source}</div>
                            <div className="text-xs text-muted-foreground">{formatDate(item.created_at)}</div>
                          </div>
                          <Badge variant="outline">v{item.persona_base_version}</Badge>
                        </div>
                        {item.notes ? <p className="mt-2 text-muted-foreground">{item.notes}</p> : null}
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Badge variant="secondary">{item.cultural_register}</Badge>
                          {(item.enabled_skills ?? []).slice(0, 3).map((skill) => <Badge key={skill} variant="outline">{skill}</Badge>)}
                          {itemIntegrity.slice(0, 2).map((flag) => <Badge key={flag} variant="outline">{flag}</Badge>)}
                        </div>
                      </div>
                    );
                  }) : <p className="text-sm text-muted-foreground">Nenhuma alteração registrada ainda.</p>}
                </div>
              </ScrollArea>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-primary" /> Observabilidade v19</CardTitle>
            <CardDescription>Resumo operacional da persona compilada, traces recentes e sinais de drift.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <MetricPill label="Traces 24 recentes" value={String(traceSummary.total)} />
              <MetricPill label="Latência média" value={traceSummary.avgLatency != null ? `${Math.round(traceSummary.avgLatency)} ms` : "—"} />
              <MetricPill label="Prompt médio" value={traceSummary.avgChars != null ? `${Math.round(traceSummary.avgChars)} chars` : "—"} />
              <MetricPill label="Drift aberto" value={String(driftSummary.open)} />
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <MetricPill label="Consistency" value={latestGoldenMetrics.persona_consistency_score != null ? latestGoldenMetrics.persona_consistency_score.toFixed(2) : "—"} />
              <MetricPill label="Conflict rate" value={latestGoldenMetrics.cross_layer_conflict_rate != null ? `${(latestGoldenMetrics.cross_layer_conflict_rate * 100).toFixed(1)}%` : "—"} />
              <MetricPill label="Within bounds" value={latestGoldenMetrics.modulation_within_bounds_rate != null ? `${(latestGoldenMetrics.modulation_within_bounds_rate * 100).toFixed(1)}%` : "—"} />
              <MetricPill label="Golden runs" value={String(goldenRuns.length)} />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-md border border-border p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium"><TimerReset className="h-4 w-4" /> Runtime recente</div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>Última composição: {traceSummary.lastSeenAt ? formatDate(traceSummary.lastSeenAt) : "sem atividade"}</p>
                  <p>Último save: {historySummary.lastSavedAt ? formatDate(historySummary.lastSavedAt) : "sem histórico"}</p>
                  <p>Alterações manuais/preset: {historySummary.total} / {historySummary.presetCount} via preset</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {traceSummary.topLayers.length
                    ? traceSummary.topLayers.map(([layer, count]) => <Badge key={layer} variant="outline">{layer} · {count}</Badge>)
                    : <Badge variant="outline">sem camadas auditáveis</Badge>}
                </div>
              </div>

              <div className="rounded-md border border-border p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium"><AlertTriangle className="h-4 w-4" /> Drift detector</div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>Eventos totais: {driftSummary.total}</p>
                  <p>Score médio: {driftSummary.avgScore != null ? driftSummary.avgScore.toFixed(2) : "—"}</p>
                  <p>Última severidade: {driftSummary.last?.severity ?? "sem eventos"}</p>
                </div>
                {driftSummary.last ? (
                  <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <Badge variant={driftSummary.last.severity === "critical" || driftSummary.last.severity === "high" ? "destructive" : "secondary"}>
                        {driftSummary.last.severity}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{formatDate(driftSummary.last.detected_at)}</span>
                    </div>
                    {driftSummary.last.notes ? <p className="mt-2 text-muted-foreground">{driftSummary.last.notes}</p> : null}
                  </div>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><History className="h-5 w-5 text-primary" /> Timeline operacional</CardTitle>
            <CardDescription>Últimos eventos de drift e composições registradas para esta persona.</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[360px] pr-3">
              <div className="space-y-3">
                {driftEvents.slice(0, 6).map((event) => (
                  <div key={event.id} className="rounded-md border border-border p-3 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">Drift detectado</div>
                        <div className="text-xs text-muted-foreground">{formatDate(event.detected_at)}</div>
                      </div>
                      <Badge variant={event.severity === "critical" || event.severity === "high" ? "destructive" : "secondary"}>{event.severity}</Badge>
                    </div>
                    <div className="mt-2 text-muted-foreground">score {Number(event.drift_score).toFixed(2)} · amostra {event.sample_size}</div>
                    {event.notes ? <p className="mt-2 text-muted-foreground">{event.notes}</p> : null}
                  </div>
                ))}

                {promptTraces.slice(0, 8).map((trace) => (
                  <div key={trace.id} className="rounded-md border border-border p-3 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">Composição de prompt</div>
                        <div className="text-xs text-muted-foreground">{formatDate(trace.created_at)}</div>
                      </div>
                      <Badge variant="outline">{trace.composition_latency_ms != null ? `${Math.round(trace.composition_latency_ms)} ms` : "sem latência"}</Badge>
                    </div>
                    <p className="mt-2 text-muted-foreground line-clamp-3">{trace.final_prompt_excerpt ?? "sem excerpt"}</p>
                  </div>
                ))}

                {driftEvents.length === 0 && promptTraces.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ainda não há telemetria suficiente desta persona.</p>
                ) : null}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MetricPill({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}

function SliderRow({ label, value, onChange, hint }: { label: string; value: number; onChange: (value: number) => void; hint: string }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <Label>{label}</Label>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </div>
      <Slider value={[value]} onValueChange={(next) => onChange(next[0])} min={0} max={100} step={5} />
    </div>
  );
}
